/**
 * Browser-Merker der Baustellenordnung: welche Ansicht zuletzt gewaehlt war —
 * „Geführt" (Vorgabe, Plan E4) oder „Alle Bereiche".
 *
 * Hier liegt NUR Bequemlichkeit. Welche Karte der gefuehrte Dialog zeigt,
 * rechnet er immer aus den Daten; faellt der Speicher aus, oeffnet das Modul
 * gefuehrt, mehr nicht.
 *
 * `localStorage` kann fehlen oder werfen (privates Fenster, App-WebView,
 * Quota). Deshalb steht JEDER Zugriff in try/catch, und ein Fehler wird
 * geschluckt. Unlesbares JSON oder eine fremde Form gilt als leer. Speicher
 * injiziert (`SpeicherLike`), damit die Tests ohne DOM laufen — Muster
 * `planungsphase/leitfadenSpeicher.ts`.
 *
 * Ein JSON-Objekt unter EINEM Schluessel: `{ ansicht?: 'gefuehrt' | 'alle' }`.
 */

export type BauordnungAnsicht = 'gefuehrt' | 'alle';

export interface SpeicherLike {
    getItem(k: string): string | null;
    setItem(k: string, v: string): void;
}

export const BAUORDNUNG_ANSICHT_SCHLUESSEL = 'osia_bauordnung_ansicht_v1';

export const ANSICHT_VORGABE: BauordnungAnsicht = 'gefuehrt';

/** `window.localStorage`, oder `null`, wo es keins gibt oder schon der Zugriff wirft. */
export const browserSpeicher = (): SpeicherLike | null => {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch {
        return null;
    }
};

const istAnsicht = (v: unknown): v is BauordnungAnsicht => v === 'gefuehrt' || v === 'alle';

/** Die gemerkte Ansicht — ohne Merker, bei kaputtem Inhalt oder werfendem Speicher die Vorgabe. */
export const ladeAnsicht = (speicher: SpeicherLike | null = browserSpeicher()): BauordnungAnsicht => {
    if (!speicher) return ANSICHT_VORGABE;
    try {
        const roh = speicher.getItem(BAUORDNUNG_ANSICHT_SCHLUESSEL);
        if (!roh) return ANSICHT_VORGABE;
        const wert: unknown = JSON.parse(roh);
        const ansicht = wert && typeof wert === 'object' ? (wert as { ansicht?: unknown }).ansicht : undefined;
        return istAnsicht(ansicht) ? ansicht : ANSICHT_VORGABE;
    } catch {
        return ANSICHT_VORGABE;
    }
};

/** Merkt die Ansicht; ein Fehler des Speichers ist nie ein Fehler der App. */
export const merkeAnsicht = (
    ansicht: BauordnungAnsicht,
    speicher: SpeicherLike | null = browserSpeicher(),
): void => {
    if (!speicher || !istAnsicht(ansicht)) return;
    try {
        speicher.setItem(BAUORDNUNG_ANSICHT_SCHLUESSEL, JSON.stringify({ ansicht }));
    } catch {
        // Quota oder privates Fenster — der Merker ist Bequemlichkeit.
    }
};
